import React, { useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import emailjs from '@emailjs/browser'; // Import EmailJS

const AICTASection = () => {
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: '',
    institute: '',
    programInterest: '',
    questions: ''
  });

  // Modal handlers
  const handleShowModal = () => setShowModal(true);

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      name: '', email: '', phone: '', category: '',
      institute: '', programInterest: '', questions: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Updated handleSubmit function
  const handleSubmit = (e) => {
    e.preventDefault();

    // 1. Dynamically build the email body as a plain string
    const emailBody = `
Personal Details:
- Name: ${formData.name}
- Email: ${formData.email}
- Phone: ${formData.phone || 'Not provided'}

Profile:
- I am a: ${formData.category}
- Institute/Organization: ${formData.institute || 'Not provided'}

Program Interest:
- Looking for: ${formData.programInterest}
- Questions/Interests: ${formData.questions || 'None'}
    `;

    // 2. Prepare the parameters for the universal template
    const templateParams = {
      email_subject: 'Looking for AI Programs to join/conduct',
      email_body: emailBody.trim() // Use trim() to remove leading whitespace
    };

    // 3. Use your EmailJS credentials
    const serviceID = 'service_n36e8hg';
    const templateID = 'template_i9jkfri';
    const publicKey = 'VAEdCMXuUKeCOw6gb';

    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        alert('Thank you for your interest! We will contact you soon.');
        handleCloseModal();
      })
      .catch((err) => {
        console.error('FAILED...', err);
        alert('Failed to send the request. Please try again later.');
      });
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #1a1a2e 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '4rem 2rem',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    
    backgroundPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(circle at 20% 20%, rgba(255,255,255,0.02) 1px, transparent 1px),
        radial-gradient(circle at 80% 80%, rgba(255,255,255,0.02) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.01) 1px, transparent 1px),
        linear-gradient(rgba(255,255,255,0.01) 1px, transparent 1px)
      `,
      backgroundSize: '100px 100px, 150px 150px, 50px 50px, 50px 50px',
      animation: 'subtleMove 30s ease-in-out infinite'
    },
    
    contentWrapper: {
      textAlign: 'center',
      maxWidth: '900px',
      width: '100%',
      position: 'relative',
      zIndex: 2
    },
    
    title: {
      fontSize: 'clamp(2.5rem, 7vw, 4.5rem)',
      fontWeight: '700',
      color: 'white',
      lineHeight: '1.2',
      marginBottom: '2rem',
      background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      textShadow: '0 0 30px rgba(255,255,255,0.1)',
      animation: 'fadeInUp 1s ease-out'
    },
    
    subtitle: {
      fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)',
      color: 'rgba(255,255,255,0.75)',
      lineHeight: '1.6',
      marginBottom: '3rem',
      fontWeight: '400',
      animation: 'fadeInUp 1s ease-out 0.2s both'
    },
    
    buttonContainer: {
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
      animation: 'fadeInUp 1s ease-out 0.4s both'
    },
    
    primaryButton: {
      background: 'linear-gradient(45deg, #4a5568 0%, #2d3748 100%)',
      color: 'white',
      padding: '1rem 2.5rem',
      borderRadius: '8px',
      fontSize: '1.1rem',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
      position: 'relative',
      overflow: 'hidden'
    },
    
    secondaryButton: {
      background: 'transparent',
      color: 'rgba(255,255,255,0.9)',
      padding: '1rem 2rem',
      borderRadius: '8px',
      fontSize: '1.1rem',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    
    arrow: {
      fontSize: '1.2rem',
      transition: 'transform 0.3s ease'
    },
    
    glowEffect: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: '300px',
      height: '300px',
      background: 'radial-gradient(circle, rgba(74, 85, 104, 0.1) 0%, transparent 70%)',
      transform: 'translate(-50%, -50%)',
      borderRadius: '50%',
      animation: 'pulse 4s ease-in-out infinite',
      zIndex: 1
    },
    
    floatingElements: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: 1
    },
    
    floatingDot: {
      position: 'absolute',
      width: '4px',
      height: '4px',
      background: 'rgba(255,255,255,0.3)',
      borderRadius: '50%',
      animation: 'float 8s ease-in-out infinite'
    },
    
    dot1: {
      top: '20%',
      left: '15%',
      animationDelay: '0s'
    },
    
    dot2: {
      top: '30%',
      right: '20%',
      animationDelay: '2s'
    },
    
    dot3: {
      bottom: '25%',
      left: '25%',
      animationDelay: '4s'
    },
    
    dot4: {
      bottom: '35%',
      right: '15%',
      animationDelay: '6s'
    }
  };

  const handlePrimaryHover = (e) => {
    e.target.style.transform = 'translateY(-2px)';
    e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.4)';
    e.target.style.background = 'linear-gradient(45deg, #5a6578 0%, #3d4758 100%)';
  };

  const handlePrimaryLeave = (e) => {
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.3)';
    e.target.style.background = 'linear-gradient(45deg, #4a5568 0%, #2d3748 100%)';
  };

  return (
    <>
      <div style={styles.container}>
        <style>
          {`
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            @keyframes subtleMove {
              0%, 100% { transform: translateX(0) translateY(0); }
              25% { transform: translateX(-10px) translateY(-5px); }
              50% { transform: translateX(10px) translateY(10px); }
              75% { transform: translateX(-5px) translateY(5px); }
            }
            
            @keyframes pulse {
              0%, 100% { 
                opacity: 0.4; 
                transform: translate(-50%, -50%) scale(1);
              }
              50% { 
                opacity: 0.6; 
                transform: translate(-50%, -50%) scale(1.1);
              }
            }
            
            @keyframes float {
              0%, 100% { 
                transform: translateY(0px);
                opacity: 0.3;
              }
              50% { 
                transform: translateY(-20px);
                opacity: 0.8;
              }
            }
          `}
        </style>
        
        <div style={styles.backgroundPattern}></div>
        <div style={styles.glowEffect}></div>
        
        <div style={styles.floatingElements}>
          <div style={{...styles.floatingDot, ...styles.dot1}}></div>
          <div style={{...styles.floatingDot, ...styles.dot2}}></div>
          <div style={{...styles.floatingDot, ...styles.dot3}}></div>
          <div style={{...styles.floatingDot, ...styles.dot4}}></div>
        </div>
        
        <div style={styles.contentWrapper}>
          <h1 style={styles.title}>
            Ready to Shape<br />
            the Future of AI?
          </h1>
          
          <p style={styles.subtitle}>
            Join thousands of students, researchers, and professionals who 
            are already part of our global AI community. Start your journey 
            today.
          </p>
          
          <div style={styles.buttonContainer}>
            <button 
              style={styles.primaryButton}
              onMouseEnter={handlePrimaryHover}
              onMouseLeave={handlePrimaryLeave}
              onClick={handleShowModal}
            >
              Get Started Today
            </button>
          </div>
        </div>
      </div>

      {/* Bootstrap Modal for Program Inquiry */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton style={{ 
          borderBottom: '1px solid #e2e8f0', 
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
        }}>
          <Modal.Title style={{ 
            fontSize: '1.5rem', 
            fontWeight: '700', 
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            🚀 Join Our AI Programs
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ padding: '2rem', background: '#ffffff' }}>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Full Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4a5568'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Email Address *
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@example.com"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4a5568'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Phone Number
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4a5568'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    I am a *
                  </Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4a5568'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  >
                    <option value="">Select category</option>
                    <option value="Educator/Teacher">Educator/Teacher</option>
                    <option value="Working Professional">Working Professional</option>
                    <option value="Researcher">Researcher</option>
                    <option value="Student">Student</option>
                    <option value="Entrepreneur">Entrepreneur</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Institute/Organization *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="institute"
                    value={formData.institute}
                    onChange={handleInputChange}
                    required
                    placeholder="Your institution or company"
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4a5568'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Which program are you looking for? *
                  </Form.Label>
                  <Form.Select
                    name="programInterest"
                    value={formData.programInterest}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4a5568'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  >
                    <option value="">Select program level</option>
                    <option value="Foundational">Foundational</option>
                    <option value="Professional">Professional</option>
                    <option value="Advanced">Advanced</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                Questions or Specific Interests
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="questions"
                value={formData.questions}
                onChange={handleInputChange}
                placeholder="Tell us about your AI interests, goals, or any specific questions you have about our programs..."
                style={{ 
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.2s ease',
                  resize: 'vertical'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4a5568'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </Form.Group>

            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'flex-end',
              paddingTop: '1rem',
              borderTop: '1px solid #e2e8f0'
            }}>
              <Button 
                variant="outline-secondary" 
                onClick={handleCloseModal}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  border: '2px solid #e2e8f0',
                  color: '#6b7280'
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  background: 'linear-gradient(45deg, #4a5568 0%, #2d3748 100%)',
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(74, 85, 104, 0.3)'
                }}
              >
                Submit Inquiry
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AICTASection;
